import { useRef } from 'react';
import { useLocation, Outlet } from 'react-router-dom';
import { AuthHeader } from './AuthHeader';
import { AuthModeToggle } from './AuthModeToggle';

export const REVEAL_EASE = 'cubic-bezier(0.22,1,0.36,1)';

/**
 * Entrance choreography, expressed entirely as CSS keyframes with staggered
 * `animation-delay` — no JS setTimeout/setState chain, so nothing here
 * re-renders after mount.
 *
 * The shell's own violet background is a separate div from its content, and
 * that background div never animated — a translateX slide on the content
 * doesn't stop the backdrop underneath it from just popping in instantly,
 * which reads as a hitch no matter how smooth the slide itself is (this was
 * confirmed on-device via screen recording). So the whole shell — backdrop
 * included — fades in as one plain opacity transition instead of sliding.
 * Content still reveals on its own staggered timeline on top of that,
 * rising into place: the illustration first, then the logo settles, the
 * headline rises line by line, then the toggle/form rise together last.
 */
const KEYFRAMES = `
@keyframes sk-auth-fade{from{opacity:0}to{opacity:1}}
@keyframes sk-fade-in{from{opacity:0}to{opacity:1}}
@keyframes sk-fade-scale{from{opacity:0;transform:scale(.97)}to{opacity:1;transform:scale(1)}}
@keyframes sk-rise{from{opacity:0;transform:translateY(6px) scale(.99)}to{opacity:1;transform:translateY(0) scale(1)}}
`;

/**
 * Shared shell for the login/register screens — renders the
 * illustration/logo/headline once via a pathless layout route, so it's the
 * SAME mounted instance across both; only the `<Outlet/>` content (the
 * form) swaps when navigating between them.
 *
 * On first mount, the whole shell fades in and its content reveals in a
 * fixed sequence (see KEYFRAMES above). This only ever plays once: the
 * shell doesn't remount when toggling between login and register, so
 * switching modes still uses only the lighter per-route fade below.
 *
 * The header sits at a fixed offset from the top (not vertically centered as
 * part of one block with the content below it) so its position depends only
 * on the viewport, never on how tall the bottom section is — centering the
 * whole block made the header visibly shift up/down between the short
 * chooser and the taller form. The offset is a deliberate fixed value (not
 * flex auto-centering) purely to give it breathing room instead of sitting
 * flush under the status bar. The Sign in/Register toggle lives here too
 * (not per-screen) — same reasoning as the header.
 */
export function AuthLayout() {
  const { pathname } = useLocation();
  const reducedMotion = useRef(
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ).current;

  return (
    <div
      className="min-h-dvh w-full"
      data-kb-backdrop="violet"
      style={{
        background: 'rgb(var(--violet))',
        animation: reducedMotion ? undefined : `sk-fade-in 480ms ${REVEAL_EASE} both`,
      }}
    >
      <style>{KEYFRAMES}</style>
      <div className="min-h-dvh flex flex-col items-center pt-safe-or-12 pb-safe-or-6 px-safe-or-6">
        <div className="w-full max-w-sm">
          <AuthHeader reducedMotion={reducedMotion} />
          <div style={{ height: 32 }} />
          <div
            style={{
              animation: reducedMotion ? undefined : `sk-rise 360ms ${REVEAL_EASE} 400ms both`,
            }}
          >
            <AuthModeToggle />
            <div key={pathname} style={{ animation: 'sk-auth-fade .3s ease both' }}>
              <Outlet />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthLayout;
